<template>
	<!-- eslint-disable max-lines-->
	<div class="cassie-vertical-md mt-1">
		<ChannelDetailedHistory
			:is-open="showChannelHistory"
			:channel-name="selectedChannelName"
			:selected-preference-id="selectedPreferenceId"
			:channel-preferences="filteredChannelPreferences"
			@close="closeChannelHistory"
		/>
		<DataSubjectBackButton @click="$emit('exit')" />
		<div
			class="d-flex align-center cassie-horizontal-md"
			style="flex: 1;"
		>
			<SecondaryActionButton
				:input-value="showFilters"
				@click="showFilters = !showFilters"
			>
				<v-icon>
					mdi-filter
				</v-icon>
				Filter
			</SecondaryActionButton>
			<template v-if="showFilters">
				<Dropdown
					v-if="showCsPortalBrandSelector"
					v-model="selectedBrandId"
					:label="`${brandFieldLabel}`"
					:items="brandFilterItems"
					:validate-field="false"
					class="cassie-input-width-xs"
				/>
				<TextField
					v-model="channelNameSearchQuery"
					label="Channel"
					:validate-field="false"
					:clearable="true"
					class="cassie-input-width-xs"
				/>
				<Dropdown
					v-if="parentChannelFilterItems.length"
					v-model="selectedParentChannelId"
					label="Parent Channel"
					:items="[{text: 'View All', value: null}, ...parentChannelFilterItems]"
					:validate-field="false"
					class="cassie-input-width-xs"
				/>
			</template>
		</div>
		<v-card
			v-if="uniqueDates.length > 1"
			class="timeline-card mb-6"
			outlined
		>
			<v-card-title class="consent-section-title">
				<v-icon class="mr-2">
					mdi-calendar-clock
				</v-icon>
				Timeline
			</v-card-title>
			<v-card-text>
				<div class="d-flex align-center mb-3">
					<div class="text-body-1 mr-2">
						View history as of:
					</div>
					<div class="selected-date text-body-1 font-weight-bold">
						{{ selectedDateIndex !== null && uniqueDates[selectedDateIndex] ? formatDate(uniqueDates[selectedDateIndex]) : 'Current Status' }}
					</div>
				</div>
				<div class="timeline-key mb-3">
					<div class="key-item">
						<span class="key-dot submission-dot" />
						<span class="key-label">
							Submission
						</span>
					</div>
					<div class="key-item">
						<span class="key-dot expiry-dot" />
						<span class="key-label">
							Expiry
						</span>
					</div>
					<div class="key-item">
						<span class="key-dot contact-dot" />
						<span class="key-label">
							Contact Update
						</span>
					</div>
					<div class="key-item">
						<span class="key-dot merge-dot" />
						<span class="key-label">
							Merge
						</span>
					</div>
					<div class="key-item">
						<span class="key-dot multi-dot" />
						<span class="key-label">
							Multiple Events
						</span>
					</div>
				</div>
				<div
					ref="timelineSlider"
					class="timeline-slider"
					:style="timelineContainerStyle"
				>
					<v-slider
						v-model="selectedDateIndex"
						:min="0"
						:max="uniqueDates.length - 1"
						:tick-labels="dateTickLabels"
						:ticks="true"
						tick-size="4"
						track-color="grey lighten-2"
						color="primary"
						:class="['timeline-slider__control', { 'expiry-thumb': isCurrentPositionExpiry, 'contact-thumb': isCurrentPositionContactUpdate, 'merge-thumb': isCurrentPositionMerge }]"
						:style="timelineSliderStyle"
					/>
				</div>
			</v-card-text>
		</v-card>
		<ContactDetailChanges
			:stand-dak-changes="standDakChanges"
			:selected-date="selectedDateIndex !== null && uniqueDates[selectedDateIndex] ? uniqueDates[selectedDateIndex] : null"
		/>
		<MergedRecordDisplay
			:merged-records="mergedRecords"
			:selected-date="selectedDateIndex !== null && uniqueDates[selectedDateIndex] ? uniqueDates[selectedDateIndex] : new Date()"
		/>
		<!-- Side by Side Layout: Current Consents and History -->
		<div class="consent-display-layout">
			<!-- Current Consent Status (Left Side - 2 Columns) -->
			<v-card
				class="current-consent-wrapper"
				outlined
			>
				<v-card-title class="consent-section-title">
					<v-icon
						color="success"
						class="mr-2"
					>
						mdi-check-circle
					</v-icon>
					Current Consent Status
				</v-card-title>
				<v-card-text>
					<v-row
						v-if="currentConsentStatus.length > 0"
					>
						<v-col
							v-for="consent in currentConsentStatus"
							:key="`${consent.brandId}-${consent.channelId}-${consent.parentChannelId || 'none'}`"
							cols="6"
						>
							<v-card
								class="consent-card clickable"
								outlined
								height="100%"
								@click="openChannelHistory(consent.channelId, consent.channelName, consent.preferenceId)"
							>
								<v-card-text class="consent-card-content">
									<div class="consent-card-header">
										<div class="consent-brand-channel">
											<div
												class="font-weight-bold"
												style="font-size: 0.85rem; line-height: 1.2;"
											>
												{{ consent.channelName }}
											</div>
											<div
												class="text-caption text--secondary"
												style="font-size: 0.7rem;"
											>
												{{ consent.brandName }}
											</div>
											<div
												v-if="consent.parentChannelName"
												class="text-caption text--secondary"
												style="font-size: 0.7rem; line-height: 1.2;"
											>
												Parent: {{ consent.parentChannelName }}
											</div>
										</div>
										<div
											class="d-flex align-center gap-1 ml-auto"
											style="flex-shrink: 0;"
										>
											<v-chip
												v-if="consent.isNewOnSelectedDate"
												color="primary"
												x-small
												label
											>
												New
											</v-chip>
											<v-chip
												:color="consent.preferenceValue === 'Opt-In' ? 'success' : 'error'"
												x-small
												label
												class="ml-1"
											>
												{{ consent.preferenceValue === 'Opt-In' ? 'Opted In' : 'Opted Out' }}
											</v-chip>
											<IconButton
												color="primary"
												class="ml-2"
												tooltip-text="View History"
												tooltip-class="history-icon-tooltip"
											>
												mdi-history
											</IconButton>
										</div>
									</div>
									<div
										class="consent-date text-caption text--secondary mt-2"
										style="font-size: 0.65rem;"
									>
										Last updated: {{ consent.dateSubmitted }}
									</div>
									<v-chip
										v-if="consent.mergedStandDak"
										color="orange"
										x-small
										label
										class="mt-2"
									>
										<v-icon
											x-small
											left
										>
											mdi-merge
										</v-icon>
										Merged
									</v-chip>
								</v-card-text>
							</v-card>
						</v-col>
					</v-row>
					<div
						v-else
						class="text-center text--secondary py-4"
					>
						No current consents
					</div>
				</v-card-text>
			</v-card>
			<!-- Consent History (Right Side - Single Column) -->
			<v-card
				class="consent-history-wrapper"
				outlined
			>
				<v-card-title class="consent-section-title">
					<v-icon
						color="orange"
						class="mr-2"
					>
						mdi-history
					</v-icon>
					Consent History
					<span class="text-caption text--secondary ml-2">
						(Superseded & Expired)
					</span>
				</v-card-title>
				<v-card-text>
					<v-row
						v-if="consentHistory.length > 0"
					>
						<v-col
							v-for="(consent, index) in consentHistory"
							:key="`history-${consent.brandId}-${consent.channelId}-${consent.preferenceId}-${index}`"
							cols="12"
						>
							<v-card
								class="consent-history-card clickable"
								outlined
								@click="openChannelHistory(consent.channelId, consent.channelName, consent.preferenceId)"
							>
								<v-card-text class="consent-card-content">
									<div class="consent-card-header">
										<div class="consent-brand-channel">
											<div class="text-body-2 font-weight-bold">
												{{ consent.channelName }}
											</div>
											<div
												class="text-caption text--secondary"
												style="font-size: 0.7rem;"
											>
												{{ consent.brandName }}
											</div>
											<div
												v-if="consent.parentChannelName"
												class="text-caption text--secondary"
												style="font-size: 0.7rem;"
											>
												Parent: {{ consent.parentChannelName }}
											</div>
										</div>
										<div class="d-flex align-center">
											<v-chip
												:color="consent.preferenceValue === 'Opt-In' ? 'success' : 'error'"
												x-small
												label
												outlined
											>
												{{ consent.preferenceValue === 'Opt-In' ? 'Opted In' : 'Opted Out' }}
											</v-chip>
											<IconButton
												color="primary"
												class="ml-2"
												tooltip-text="View History"
												tooltip-class="history-icon-tooltip"
											>
												mdi-history
											</IconButton>
										</div>
									</div>
									<div
										class="consent-date text-caption text--secondary mt-2"
										style="font-size: 0.65rem;"
									>
										Date: {{ consent.dateSubmitted }}
									</div>
									<v-chip
										v-if="consent.mergedStandDak"
										color="orange"
										x-small
										label
										outlined
										class="mt-2"
									>
										<v-icon
											x-small
											left
										>
											mdi-merge
										</v-icon>
										Merged
									</v-chip>
									<div
										v-if="consent.supersededBy"
										class="text-caption mt-1"
										style="font-size: 0.65rem; color: #e67e22;"
									>
										Superseded by newer consent
									</div>
									<div
										v-else-if="consent.expired"
										class="text-caption mt-1"
										style="font-size: 0.65rem; color: #e74c3c;"
									>
										Expired{{ consent.validTo ? ` on ${consent.validTo}` : '' }}
									</div>
								</v-card-text>
							</v-card>
						</v-col>
					</v-row>
					<div
						v-else
						class="text-center text--secondary py-4"
					>
						No historical consents
					</div>
				</v-card-text>
			</v-card>
		</div>
	</div>
</template>

<script>
/* eslint-disable max-lines */
import DataSubjectBackButton from './data-subject-back-button.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import IconButton from '../../../../../../shared/components/icon-button.vue'
import Dropdown from '../../../../../../shared/components/dropdown.vue'
import TextField from '../../../../../../shared/components/text-field.vue'
import { brandFieldLabel, contactDetailsFields } from '../../../../../../shared/state/configuration.js'
import { showCsPortalBrandSelector, csPortalBrandSelector, UNIVERSAL_BRAND } from '../../../../../../shared/state/brands.js'
import { getDataSubjectHistory } from '../../../../../../shared/utils/api/data-subject.js'
import ChannelDetailedHistory from './data-subject-preferences/channel-detailed-history.vue'
import ContactDetailChanges from './data-subject-preferences/contact-detail-changes.vue'
import MergedRecordDisplay from './data-subject-preferences/merged-record-display.vue'

export default {
	components: { DataSubjectBackButton, SecondaryActionButton, IconButton, Dropdown, TextField, ChannelDetailedHistory, ContactDetailChanges, MergedRecordDisplay },
	props: { syrenisId: Number, searchBrandId: Number },
	setup () { return { brandFieldLabel, contactDetailsFields, showCsPortalBrandSelector, csPortalBrandSelector, UNIVERSAL_BRAND } },
	data () {
		return {
			channelNameSearchQuery: '',
			selectedBrandId: 0,
			isCurrentValue: null,
			selectedParentChannelId: null,
			showFilters: false,
			preferences: [],
			mergedRecords: [],
			mergedPreferences: [],
			profiles: [],
			standDak: [],
			selectedDateIndex: null,
			showChannelHistory: false,
			selectedChannelId: null,
			selectedChannelName: '',
			selectedPreferenceId: null,
			showContactDetails: false,
			tickUpdateTimeouts: []
		}
	},
	computed: {
		filteredStandDaksWithChanges () {
			// If no standDaks have any date field, just return all of them
			const hasDateField = this.standDak.some(sd => this.getStandDakDate(sd))
			if (!hasDateField) {
				return [...this.standDak]
			}

			const sortedStandDaks = [...this.standDak]
				.filter(sd => this.getStandDakDate(sd))
				.sort((a, b) => new Date(this.getStandDakDate(a)) - new Date(this.getStandDakDate(b)))

			return sortedStandDaks.filter((sd, index) => {
				if (index === 0) return true
				const previous = sortedStandDaks[index - 1]
				return Object.keys(sd).some(key => !this.excludedStandDakFields.includes(key) && sd[key] !== previous[key])
			})
		},
		excludedStandDakFields () {
			return ['Submission', 'Submissions', 'DateUpdatedUTC', 'DateUpdated', 'DateAdded', 'DateAddedUTC', 'SyrenisID', 'CRC', 'GUID']
		},
		uniqueDates () {
			if (this.preferences.length === 0 && this.standDak.length === 0 && this.mergedRecords.length === 0) {
				return [new Date()]
			}

			const today = new Date()
			today.setHours(0, 0, 0, 0)
			const todayTime = today.getTime()

			// Helper to check if a date is valid (not null, not NaN, not future)
			const isValidDate = time => time && !isNaN(time) && time <= todayTime

			const submissionDates = this.preferences.map(pref => {
				const date = new Date(pref.dateSubmitted)
				date.setHours(0, 0, 0, 0)
				return date.getTime()
			}).filter(isValidDate)

			const expiryDates = this.preferences
				.filter(pref => pref.validTo)
				.map(pref => {
					const date = new Date(pref.validTo)
					date.setHours(0, 0, 0, 0)
					return date.getTime()
				})
				.filter(isValidDate)

			const standDakDates = this.filteredStandDaksWithChanges
				.map(sd => {
					const dateField = this.getStandDakDate(sd)
					if (!dateField) return null
					const date = new Date(dateField)
					date.setHours(0, 0, 0, 0)
					return date.getTime()
				})
				.filter(isValidDate)

			const mergeDates = this.mergedRecords
				.map(mr => {
					if (!mr.mergeDate) return null
					const date = new Date(mr.mergeDate)
					date.setHours(0, 0, 0, 0)
					return date.getTime()
				})
				.filter(isValidDate)

			const allDateTimes = [...new Set([...submissionDates, ...expiryDates, ...standDakDates, ...mergeDates])]
			if (!allDateTimes.includes(todayTime)) {
				allDateTimes.push(todayTime)
			}
			return allDateTimes.sort((a, b) => a - b).map(time => new Date(time))
		},
		dateTickLabels () {
			const total = this.uniqueDates.length
			return this.uniqueDates.map((date, index) => {
				if (total <= 10) return this.formatDateShort(date)
				if (index === 0 || index === total - 1) return this.formatDateShort(date)
				if (total <= 30 && index % 5 === 0) return this.formatDateShort(date)
				if (total <= 100 && index % 10 === 0) return this.formatDateShort(date)
				if (total > 100 && index % 20 === 0) return this.formatDateShort(date)
				return ''
			})
		},
		needsHorizontalScroll () {
			const SCROLL_THRESHOLD = 15
			return this.uniqueDates.length > SCROLL_THRESHOLD
		},
		timelineContainerStyle () {
			return this.needsHorizontalScroll ? { overflowX: 'auto' } : {}
		},
		timelineSliderStyle () {
			const TICK_WIDTH = 60
			return this.needsHorizontalScroll ? { minWidth: `${this.uniqueDates.length * TICK_WIDTH}px` } : {}
		},
		timelineEventTypes () {
			const getTime = date => {
				const d = new Date(date)
				d.setHours(0, 0, 0, 0)
				return d.getTime()
			}
			return this.uniqueDates.map(date => {
				const dateTime = getTime(date)
				const isSubmission = this.preferences.some(pref => getTime(pref.dateSubmitted) === dateTime)
				const isExpiry = this.preferences.some(pref => pref.expiredOn && getTime(pref.expiredOn) === dateTime)
				const isContactUpdate = this.filteredStandDaksWithChanges.some(sd => sd.DateUpdatedUTC && getTime(sd.DateUpdatedUTC) === dateTime)
				const isMerge = this.mergedRecords.some(mr => getTime(mr.mergeDate) === dateTime)
				// If there's a merge, don't show multiple colors - merge takes precedence
				const hasMultiple = isMerge ? false : [isSubmission, isExpiry, isContactUpdate].filter(Boolean).length > 1
				return { isSubmission, isExpiry, isContactUpdate, isMerge, hasMultiple }
			})
		},
		isCurrentPositionExpiry () {
			if (this.selectedDateIndex !== null && this.timelineEventTypes[this.selectedDateIndex]) {
				return this.timelineEventTypes[this.selectedDateIndex].isExpiry
			}
			return false
		},
		isCurrentPositionContactUpdate () {
			if (this.selectedDateIndex !== null && this.timelineEventTypes[this.selectedDateIndex]) {
				const eventType = this.timelineEventTypes[this.selectedDateIndex]
				return eventType.isContactUpdate && !eventType.isExpiry && !eventType.isSubmission
			}
			return false
		},
		isCurrentPositionMerge () {
			if (this.selectedDateIndex !== null && this.timelineEventTypes[this.selectedDateIndex]) {
				return this.timelineEventTypes[this.selectedDateIndex].isMerge
			}
			return false
		},
		currentConsentStatus () {
			const consents = this.getConsentsAtDate(this.selectedDateIndex)
			// Mark which consents changed on the selected date
			const selectedDate = this.selectedDateIndex !== null && this.uniqueDates[this.selectedDateIndex]
				? new Date(this.uniqueDates[this.selectedDateIndex])
				: new Date()
			selectedDate.setHours(0, 0, 0, 0)

			return consents.map(consent => {
				const consentDate = new Date(consent.dateSubmitted)
				consentDate.setHours(0, 0, 0, 0)
				const isNewOnSelectedDate = consentDate.getTime() === selectedDate.getTime()
				return { ...consent, isNewOnSelectedDate }
			})
		},
		consentHistory () {
			const selectedDate = this.selectedDateIndex !== null && this.uniqueDates[this.selectedDateIndex]
				? new Date(this.uniqueDates[this.selectedDateIndex])
				: new Date()
			selectedDate.setHours(23, 59, 59, 999)
			const selectedDateStart = new Date(selectedDate)
			selectedDateStart.setHours(0, 0, 0, 0)
			const today = new Date()
			today.setHours(23, 59, 59, 999)
			const currentConsentKeys = new Set(this.currentConsentStatus.map(c =>
				`${c.brandId}-${c.channelId}-${c.parentChannelId || 'none'}-${c.preferenceId}`
			))
			const historicalByChannel = {}
			let filteredPrefs = [...this.preferences]

			if (this.searchBrandId !== UNIVERSAL_BRAND) {
				filteredPrefs = filteredPrefs.filter(({ brandId }) => brandId === this.searchBrandId || brandId === UNIVERSAL_BRAND)
			}
			if (this.selectedBrandId) {
				filteredPrefs = filteredPrefs.filter(({ brandId }) => brandId === this.selectedBrandId)
			}
			if (this.channelNameSearchQuery) {
				filteredPrefs = filteredPrefs.filter(({ channelName }) =>
					channelName.toLowerCase().includes(this.channelNameSearchQuery.toLowerCase())
				)
			}
			if (this.selectedParentChannelId) {
				// Include both children (matching parentChannelId) and the parent channel itself (where channelId matches)
				filteredPrefs = filteredPrefs.filter(({ parentChannelId, channelId }) =>
					parentChannelId === this.selectedParentChannelId || channelId === this.selectedParentChannelId
				)
			}
			filteredPrefs.forEach(pref => {
				const prefDate = new Date(pref.dateSubmitted)
				if (prefDate > selectedDate) return

				const key = `${pref.brandId}-${pref.channelId}-${pref.parentChannelId || 'none'}-${pref.preferenceId}`
				if (currentConsentKeys.has(key)) return

				const validToDate = pref.validTo ? new Date(pref.validTo) : null
				if (validToDate) {
					validToDate.setHours(23, 59, 59, 999)
				}

				const hasExpired = validToDate && validToDate <= selectedDate && validToDate <= today
				const channelKey = `${pref.brandId}-${pref.channelId}-${pref.parentChannelId || 'none'}`
				const newerPref = filteredPrefs.find(p =>
					`${p.brandId}-${p.channelId}-${p.parentChannelId || 'none'}` === channelKey &&
					new Date(p.dateSubmitted) > prefDate &&
					new Date(p.dateSubmitted) <= selectedDate
				)
				const supersededBy = !!newerPref
				const expiredOnSelectedDate = hasExpired && validToDate &&
					validToDate.getTime() >= selectedDateStart.getTime() &&
					validToDate.getTime() <= selectedDate.getTime()
				const newerPrefTime = newerPref ? new Date(newerPref.dateSubmitted).getTime() : 0
				const supersededOnSelectedDate = supersededBy && newerPref &&
					newerPrefTime >= selectedDateStart.getTime() &&
					newerPrefTime <= selectedDate.getTime()

				if ((hasExpired || supersededBy) &&
					(!historicalByChannel[channelKey] || new Date(historicalByChannel[channelKey].dateSubmitted) < prefDate)) {
					historicalByChannel[channelKey] = {
						...pref,
						supersededBy,
						expired: hasExpired,
						expiredOnSelectedDate,
						supersededOnSelectedDate
					}
				}
			})
			let historicalConsents = Object.values(historicalByChannel)
			if (this.isCurrentValue !== null) {
				// Use skipCurrentFilter to avoid re-entering the current/historic filter logic inside getConsentsAtDate
				const currentKeys = new Set(this.getConsentsAtDate(this.uniqueDates.length - 1, { skipCurrentFilter: true }).map(c => `${c.brandId}-${c.channelId}-${c.parentChannelId || 'none'}`))
				historicalConsents = historicalConsents.filter(pref => {
					const key = `${pref.brandId}-${pref.channelId}-${pref.parentChannelId || 'none'}`
					return this.isCurrentValue ? currentKeys.has(key) : !currentKeys.has(key)
				})
			}
			return historicalConsents.sort((a, b) => {
				if (a.expiredOnSelectedDate !== b.expiredOnSelectedDate) return a.expiredOnSelectedDate ? -1 : 1
				if (a.supersededOnSelectedDate !== b.supersededOnSelectedDate) return a.supersededOnSelectedDate ? -1 : 1
				return new Date(b.dateSubmitted) - new Date(a.dateSubmitted)
			})
		},
		parentChannelFilterItems () {
			const parentChannels = [...new Set(this.preferences)]
				.map(({ parentChannelId, brandId, parentChannelName }) => ({ brandId, text: parentChannelName, value: parentChannelId }))
				.filter(({ text }) => text).sort((a, b) => a.text.localeCompare(b.text))
			return !this.selectedBrandId ? parentChannels : parentChannels.filter(({ brandId }) => brandId === this.selectedBrandId)
		},
		currentPreferenceFilteritems () {
			return [{ text: 'View All', value: null }, { text: 'Current', value: true }, { text: 'Historic', value: false }]
		},
		brandFilterItems () {
			return this.searchBrandId !== UNIVERSAL_BRAND
				? csPortalBrandSelector.value.filter(({ value }) => value === this.searchBrandId || value === UNIVERSAL_BRAND)
				: csPortalBrandSelector.value
		},
		filteredChannelPreferences () {
			if (!this.selectedChannelId) return []
			// Show ALL history for the channel, not filtered by selected date
			const filtered = this.preferences.filter(pref => pref.channelId === this.selectedChannelId)

			// Mark which preference is current as of TODAY (not the selected date)
			const today = new Date()
			today.setHours(23, 59, 59, 999)

			// Find the most recent preference for this channel as of today
			const currentPref = filtered
				.filter(pref => new Date(pref.dateSubmitted) <= today)
				.sort((a, b) => new Date(b.dateSubmitted) - new Date(a.dateSubmitted))[0]

			return filtered
				.map(pref => ({
					...pref,
					isCurrent: currentPref && pref.preferenceId === currentPref.preferenceId
				}))
				.sort((a, b) => new Date(b.dateSubmitted) - new Date(a.dateSubmitted))
		},
		standDakChanges () {
			if (this.standDak.length === 0) return null

			const selectedDate = this.selectedDateIndex !== null && this.uniqueDates[this.selectedDateIndex]
				? new Date(this.uniqueDates[this.selectedDateIndex])
				: new Date()
			selectedDate.setHours(23, 59, 59, 999)

			// Filter by date if any date field exists, otherwise include all
			const validStandDaks = this.filteredStandDaksWithChanges.filter(sd => {
				const dateField = this.getStandDakDate(sd)
				if (!dateField) return true // Include if no date field
				return new Date(dateField) <= selectedDate
			})

			if (validStandDaks.length === 0) return null

			const fieldHistory = {}
			validStandDaks.forEach(standDak => {
				Object.keys(standDak).forEach(key => {
					if (this.excludedStandDakFields.includes(key)) return
					const value = standDak[key]
					if (value && value !== '') {
						const lastUpdated = this.getStandDakDate(standDak) || new Date().toISOString()
						if (!fieldHistory[key] || fieldHistory[key].value !== value) {
							const configField = this.contactDetailsFields.find(f => f.fieldName.toLowerCase() === key.toLowerCase())
							fieldHistory[key] = { label: configField?.fieldLabel || key, value: value, lastUpdated }
						}
					}
				})
			})

			const fields = Object.values(fieldHistory).sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated))
			const lastStandDak = validStandDaks[validStandDaks.length - 1]
			const dateField = this.getStandDakDate(lastStandDak) || new Date().toISOString()

			return {
				date: dateField,
				submissionId: lastStandDak.Submission?.SubmissionID,
				logType: lastStandDak.Submission?.LogTypeDescription,
				fields
			}
		}
	},
	watch: {
		syrenisId (newVal) { this.getTimelineData(newVal) },
		uniqueDates: {
			handler (newDates, oldDates) {
				if (newDates.length > 0 && (this.selectedDateIndex === null || !oldDates || oldDates.length !== newDates.length)) {
					this.selectedDateIndex = newDates.length - 1
				}
				this.updateTimelineTicks()
			},
			immediate: true
		},
		timelineEventTypes () { this.updateTimelineTicks() },
		selectedDateIndex () {
			// Clear any pending timeouts before scheduling new ones
			this.tickUpdateTimeouts.forEach(id => clearTimeout(id))
			// Multiple updates needed as Vuetify renders slider ticks asynchronously
			this.tickUpdateTimeouts = [0, 50, 150].map(delay =>
				setTimeout(() => this.updateTimelineTicks(), delay)
			)
			this.scrollToSelectedTick()
		}
	},
	beforeDestroy () {
		this.tickUpdateTimeouts.forEach(id => clearTimeout(id))
	},
	created () {
		this.getTimelineData(this.syrenisId)
		this.$nextTick(() => {
			this.updateTimelineTicks()
			this.scrollToSelectedTick()
		})
	},
	methods: {
		getStandDakDate (sd) {
			return sd.DateUpdatedUTC || sd.DateAddedUTC
		},
		updateTimelineTicks () {
			if (!this.$el) return

			this.$nextTick(() => {
				this.$el.querySelectorAll('.timeline-slider .v-slider__tick').forEach((tick, index) => {
					if (!tick.dataset.clickBound) {
						tick.dataset.clickBound = 'true'
						tick.style.cursor = 'pointer'
						tick.addEventListener('click', () => {
							this.selectedDateIndex = index
						})
					}

					if (this.timelineEventTypes[index]) {
						const eventType = this.timelineEventTypes[index]
						tick.classList.remove('expiry-tick', 'both-tick', 'contact-tick', 'merge-tick', 'multi-tick')
						tick.style.background = ''

						if (eventType.hasMultiple) {
							tick.classList.add('multi-tick')
							const colors = []
							if (eventType.isSubmission) colors.push('#436A94')
							if (eventType.isExpiry) colors.push('#ff5252')
							if (eventType.isContactUpdate) colors.push('#4caf50')
							// Note: merge is not included here as it always displays on its own
							const gradient = colors.map((color, i) =>
								`${color} ${i * 360 / colors.length}deg ${(i + 1) * 360 / colors.length}deg`
							).join(', ')
							tick.style.background = `conic-gradient(from 0deg, ${gradient})`
						} else if (eventType.isExpiry) {
							tick.classList.add('expiry-tick')
						} else if (eventType.isContactUpdate) {
							tick.classList.add('contact-tick')
						} else if (eventType.isMerge) {
							tick.classList.add('merge-tick')
						}
					}
				})
			})
		},
		scrollToSelectedTick () {
			if (!this.needsHorizontalScroll) return
			this.$nextTick(() => {
				const container = this.$refs.timelineSlider
				if (!container || this.selectedDateIndex === null) return
				const ticks = container.querySelectorAll('.v-slider__tick')
				const selectedTick = ticks[this.selectedDateIndex]
				if (selectedTick) {
					selectedTick.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
				}
			})
		},
		formatDate (date) {
			if (!date) return ''
			const d = new Date(date)
			return `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' })} ${d.getFullYear()}`
		},
		formatDateShort (date) {
			if (!date) return ''
			const d = new Date(date)
			return `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' })}`
		},
		getConsentsAtDate (dateIndex, options = {}) {
			const { skipCurrentFilter = false } = options
			let preferences = [...this.preferences].sort((a, b) => new Date(b.dateSubmitted) - new Date(a.dateSubmitted))

			if (this.searchBrandId !== UNIVERSAL_BRAND) {
				preferences = preferences.filter(({ brandId }) => brandId === this.searchBrandId || brandId === UNIVERSAL_BRAND)
			}
			if (this.selectedBrandId) {
				preferences = preferences.filter(({ brandId }) => brandId === this.selectedBrandId)
			}
			if (this.channelNameSearchQuery) {
				preferences = preferences.filter(({ channelName }) =>
					channelName.toLowerCase().includes(this.channelNameSearchQuery.toLowerCase())
				)
			}
			if (this.selectedParentChannelId) {
				// Include both children (matching parentChannelId) and the parent channel itself (where channelId matches)
				preferences = preferences.filter(({ parentChannelId, channelId }) =>
					parentChannelId === this.selectedParentChannelId || channelId === this.selectedParentChannelId
				)
			}
			if (dateIndex !== null && this.uniqueDates[dateIndex]) {
				const selectedDate = new Date(this.uniqueDates[dateIndex])
				selectedDate.setHours(23, 59, 59, 999)
				const today = new Date()
				today.setHours(23, 59, 59, 999)
				const channelGroups = {}
				const processedChannels = new Set()

				preferences.forEach(pref => {
					const prefDate = new Date(pref.dateSubmitted)
					if (prefDate > selectedDate) return

					const key = `${pref.brandId}-${pref.channelId}-${pref.parentChannelId || 'none'}`
					if (processedChannels.has(key)) return

					processedChannels.add(key)
					const validToDate = pref.validTo ? new Date(pref.validTo) : null
					if (validToDate) {
						validToDate.setHours(23, 59, 59, 999)
					}

					if (!(validToDate && validToDate <= selectedDate && validToDate <= today)) {
						channelGroups[key] = pref
					}
				})
				preferences = Object.values(channelGroups)
			}
			if (this.isCurrentValue !== null && !skipCurrentFilter) {
				// Avoid infinite recursion by skipping current/historic filtering in the recursive call
				const currentKeys = new Set(this.getConsentsAtDate(this.uniqueDates.length - 1, { skipCurrentFilter: true }).map(c => `${c.brandId}-${c.channelId}-${c.parentChannelId || 'none'}`))
				preferences = preferences.filter(pref => {
					const key = `${pref.brandId}-${pref.channelId}-${pref.parentChannelId || 'none'}`
					return this.isCurrentValue ? currentKeys.has(key) : !currentKeys.has(key)
				})
			}
			return preferences
		},
		async getTimelineData (syrenisId) {
			const profile = this.profiles.find(p => p.syrenisId === syrenisId)
			if (profile) {
				return this.updateProfileData(profile)
			}

			const { data: { preferences, mergedRecords, standDak } } = await getDataSubjectHistory(syrenisId)
			this.profiles.push({ syrenisId, preferences, mergedRecords, standDak })
			this.updateProfileData(this.profiles[this.profiles.length - 1])
		},
		updateProfileData (profile) {
			this.preferences = profile.preferences
			this.mergedRecords = profile.mergedRecords || []
			this.standDak = profile.standDak || []
			// Reset mergedPreferences to avoid leaking merged data between profiles
			this.mergedPreferences = []
			this.extractMergedPrefs()
			this.combinePrefsWithMergedPrefs()
			this.markExpiredPreferences()
		},
		extractMergedPrefs () {
			this.mergedRecords.forEach(mr => {
				mr.mergedStandDaks.forEach(sd => {
					const p = mr.mergedPreferences.filter(pref => this.mergedPrefsFilter(pref, sd, mr)).map(pref => ({ ...pref, syrenisId: mr.mergedSyrenisId, mergedStandDak: sd }))
					if (p.length > 0) p.forEach(pref => { if (!this.mergedPreferences.some(mp => pref.preferenceId === mp.preferenceId)) this.mergedPreferences.push(pref) })
				})
			})
		},
		mergedPrefsFilter (pref, sd, mr) {
			if (pref.submissionId && sd.submission.submissionId === pref.submissionId) return true
			if (mr.mergedPreferences.some(parent => sd.submission.submissionId === parent.submissionId &&
				pref.parentChannelId === parent.channelId && pref.dateSubmittedUtc === parent.dateSubmittedUtc)) return true
			return !pref.submissionId && pref.dateSubmittedUtc === sd.submission.submissionDateTimeUTC
		},
		combinePrefsWithMergedPrefs () {
			this.mergedPreferences.forEach(mergedPref => {
				const idx = this.preferences.findIndex(pref => pref.preferenceId === mergedPref.preferenceId)
				if (idx >= 0) this.preferences[idx].mergedStandDak = mergedPref.mergedStandDak; else this.preferences.push(mergedPref)
			})
		},
		markExpiredPreferences () {
			const today = new Date()
			today.setHours(23, 59, 59, 999)

			this.preferences.forEach(pref => {
				if (!pref.validTo) return

				const expiryDate = new Date(pref.validTo)
				expiryDate.setHours(23, 59, 59, 999)
				if (expiryDate > today) return

				const channelKey = `${pref.brandId}-${pref.channelId}-${pref.parentChannelId || 'none'}`
				const nextPref = this.preferences
					.filter(p =>
						`${p.brandId}-${p.channelId}-${p.parentChannelId || 'none'}` === channelKey &&
						new Date(p.dateSubmitted) > new Date(pref.dateSubmitted)
					)
					.sort((a, b) => new Date(a.dateSubmitted) - new Date(b.dateSubmitted))[0]

				if (!nextPref || expiryDate < new Date(nextPref.dateSubmitted)) {
					pref.expiredOn = pref.validTo
				}
			})
		},
		openChannelHistory (channelId, channelName, preferenceId) {
			Object.assign(this, {
				selectedChannelId: channelId,
				selectedChannelName: channelName,
				selectedPreferenceId: preferenceId,
				showChannelHistory: true
			})
		},
		closeChannelHistory () {
			Object.assign(this, {
				showChannelHistory: false,
				selectedChannelId: null,
				selectedChannelName: '',
				selectedPreferenceId: null
			})
		}
	}
}

</script>

<style scoped>
.timeline-header{color:#4a5568}.timeline-key{display:flex;gap:16px;flex-wrap:wrap}.key-item{display:flex;align-items:center;gap:6px}.key-dot{width:12px;height:12px;border-radius:50%;border:2px solid white;display:inline-block}.key-dot.submission-dot{background-color:var(--global-primary);box-shadow:0 0 0 2px var(--global-primary)}.key-dot.expiry-dot{background-color:#ff5252;box-shadow:0 0 0 2px #ff1744}.key-dot.contact-dot{background-color:#4caf50;box-shadow:0 0 0 2px #4caf50}.key-dot.merge-dot{background-color:var(--global-color-orange-darken-3);box-shadow:0 0 0 2px var(--global-color-orange-darken-3)}.key-dot.multi-dot{background:conic-gradient(from 0deg,var(--global-primary) 0deg 120deg,#ff5252 120deg 240deg,#4caf50 240deg 360deg);box-shadow:0 0 4px rgba(0,0,0,0.3)}.key-label{font-size:14px;color:#4a5568}.timeline-slider__control{margin-top:20px}.timeline-slider>>>.v-slider__track-container{height:4px}.timeline-slider>>>.v-slider__thumb{width:20px;height:20px;border:3px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.2)}.timeline-slider>>>.v-slider__tick{border-radius:50%!important;background-color:var(--global-primary)!important;width:12px!important;height:12px!important;margin-left:-1px!important;margin-top:-3px!important;border:2px solid white!important;box-shadow:0 0 0 2px var(--global-primary),0 2px 6px rgba(67,106,148,0.4)!important}.timeline-slider>>>.v-slider__tick.expiry-tick{background-color:#ff5252!important;box-shadow:0 0 0 2px #ff1744,0 2px 8px rgba(255,23,68,0.5)!important}.timeline-slider>>>.v-slider__tick.both-tick,.timeline-slider>>>.v-slider__tick.multi-tick{width:14px!important;height:14px!important;margin-left:-1.5px!important;margin-top:-4px!important;box-shadow:0 0 6px rgba(0,0,0,0.4)!important}.timeline-slider>>>.v-slider__tick.both-tick{background:linear-gradient(135deg,var(--global-primary) 50%,#ff5252 50%)!important}.timeline-slider>>>.v-slider__tick.contact-tick{background-color:#4caf50!important;box-shadow:0 0 0 2px #4caf50,0 2px 6px rgba(76,175,80,0.4)!important}.timeline-slider>>>.v-slider__tick.merge-tick{background-color:var(--global-color-orange-darken-3)!important;box-shadow:0 0 0 2px var(--global-color-orange-darken-3),0 2px 6px rgba(255,193,7,0.4)!important}.timeline-slider>>>.v-slider__tick-label{font-size:11px;color:#637083}.expiry-thumb>>>.v-slider__thumb,.expiry-thumb>>>.v-slider__thumb-container--active .v-slider__thumb{background-color:#ff5252!important;border-color:#ff5252!important}.expiry-thumb>>>.v-slider__thumb::before{background-color:#ff5252!important;opacity:0.2}.expiry-thumb>>>.v-slider__thumb::after{display:none!important}.expiry-thumb>>>.v-slider__track-fill{background-color:var(--global-primary)!important}.contact-thumb>>>.v-slider__thumb,.contact-thumb>>>.v-slider__thumb-container--active .v-slider__thumb{background-color:#4caf50!important;border-color:#4caf50!important}.contact-thumb>>>.v-slider__thumb::before{background-color:#4caf50!important;opacity:0.2}.contact-thumb>>>.v-slider__thumb::after{display:none!important}.contact-thumb>>>.v-slider__track-fill{background-color:var(--global-primary)!important}.merge-thumb>>>.v-slider__thumb,.merge-thumb>>>.v-slider__thumb-container--active .v-slider__thumb{background-color:var(--global-color-orange-darken-3)!important;border-color:var(--global-color-orange-darken-3)!important}.merge-thumb>>>.v-slider__thumb::before{background-color:var(--global-color-orange-darken-3)!important;opacity:0.2}.merge-thumb>>>.v-slider__thumb::after{display:none!important}.merge-thumb>>>.v-slider__track-fill{background-color:var(--global-primary)!important}.consent-display-layout{display:flex;gap:24px}.current-consent-wrapper{flex:2;min-width:0}.consent-history-wrapper{flex:1;min-width:0}.timeline-card{width:100%}.clickable{cursor:pointer}@media (max-width:1200px){.consent-display-layout{flex-direction:column}}.consent-card{border:1px solid var(--global-border-color);border-radius:var(--radius,8px);transition:all 0.2s ease}.consent-card:hover{box-shadow:0 4px 12px rgba(0,0,0,0.15);border-color:var(--global-primary);background-color:var(--global-main-background-color)}.consent-history-card{border:1px solid var(--global-main-background-color);border-radius:var(--radius,8px)}.consent-history-card:hover{box-shadow:0 4px 8px rgba(0,0,0,0.12);border-color:var(--global-primary);background-color:var(--global-main-background-color)}.consent-card-content{padding:16px!important}.consent-card-header{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}.consent-brand-channel{flex:1;min-width:0}.consent-date{margin-top:8px;padding-top:8px;border-top:1px solid var(--global-border-color)}.consent-section-title{font-size: 1.1rem !important;}
</style>

<style>
.history-icon-tooltip {
	white-space: nowrap;
	width: auto !important;
	max-width: none !important;
}
</style>
